import { create } from 'zustand';
import { ApiError, request } from '@/lib/api';
import { TaxRegime } from '@/lib/states';
import { NO_DEFAULT_CHARGES, type DefaultCharge } from '@/lib/charges';

/**
 * The preferences that decide how the screens behave, rather than what the server
 * holds.
 *
 * Kept in the browser, per installation, and deliberately so: every one of these is
 * a question about how this workstation should behave — whether to call a
 * translation service, which tax rates to offer on a rate picker, how many rows a
 * page holds — and none of them changes what any document means. The firm's regime
 * and its own state are the exception in kind: they belong on the firm record and
 * the API does not expose it yet, so they are set here and the screens that need
 * them read them from here. When the firm endpoint arrives this store reads from it
 * instead and nothing else moves.
 */
/**
 * Where a sale's line rate comes from: one of the product's own rates, the customer's
 * last quotation, the customer's last sale, or the selling rate of the batch picked.
 * The last three start from retail where there is nothing to take.
 */
export type SalesRateType =
  'retail' | 'wholesale' | 'mrp' | 'lastQuotation' | 'customerRate' | 'batch';

/**
 * One way a customer pays online - Google Pay, PhonePe, Talabat - offered on a sale paid
 * online. The firm's own list, kept with its settings and named on each invoice it paid.
 */
export interface OnlinePaymentType {
  readonly id: string;
  readonly name: string;
  /** The bank account it settles into; blank uses the default bank account. */
  readonly ledgerId: string;
  /** Offered on new sales. Off, it stays on the bills already paid with it. */
  readonly active: boolean;
}

/** The units a sale's total can be rounded to. Zero rounds to the currency alone. */
export const ROUND_TO_CHOICES: readonly number[] = [0, 0.05, 0.1, 0.25, 0.5, 1, 5, 10];

export interface AppSettings {
  /**
   * Whether an Arabic name is filled in automatically from the English one.
   *
   * Off by default. It sends the text somebody typed to a translation service, and
   * that is a decision a firm makes rather than one made for them.
   */
  readonly autoTranslateArabic: boolean;
  /**
   * A Google Cloud Translation API key.
   *
   * Blank uses the public endpoint, which needs no key and is rate-limited by
   * Google as it sees fit. A firm translating a catalogue supplies its own.
   */
  readonly translationApiKey: string;
  /** The regime the firm charges under, which decides the state table and the heads. */
  readonly taxRegime: number;
  /** The firm's own state, which decides intra-state against inter-state. */
  readonly firmStateCode: string;
  /** The tax rates a rate picker offers, in the order it offers them. */
  readonly taxRates: readonly number[];
  /** The rate a new document line starts at. */
  readonly defaultTaxRate: number;
  /**
   * The warehouse a new document starts at, when the master has no default of its
   * own or the user prefers another.
   */
  readonly preferredWarehouseId: string;
  /**
   * The category and the unit of measure a new product starts at.
   *
   * A firm that sells one kind of thing enters the same category on every product
   * it ever adds, and the same unit on most of them. Both stay mandatory — the
   * server needs them and the form still marks them — but a mandatory field that
   * already holds the right answer is one fewer dropdown between somebody and the
   * product they came to add. Blank means "ask", which is what a firm with a real
   * catalogue wants.
   */
  readonly defaultCategoryId: string;
  readonly defaultStockUnitId: string;
  /**
   * The payment mode a new receipt or payment starts at.
   *
   * A counter that takes cash all day was retyping "Cash" onto every voucher. Blank
   * means "ask", which is what a firm taking money four different ways wants.
   */
  readonly defaultPaymentMode: string;
  /**
   * The charge heads a new document starts with, keyed by document kind.
   *
   * Freight on every purchase, delivery on every invoice, a standing discount — the
   * heads a firm puts on the same documents every day and was adding by hand each
   * time, with the row forgotten often enough to matter. Held here rather than on
   * the server because the server has no endpoint for it: the charge matrix it does
   * hold says which way a head moves a total, not which heads a document starts
   * with.
   */
  readonly defaultCharges: Readonly<Record<string, readonly DefaultCharge[]>>;
  /** How many rows a paged list holds. */
  readonly pageSize: number;
  /**
   * How many decimal places money is shown to, when a branch says nothing else.
   *
   * Two for most of the world, three where a currency subdivides by a thousand —
   * the dinars of Bahrain, Kuwait, Jordan and Iraq, and the Omani rial — and zero
   * where it does not subdivide at all, which is how a yen or a franc price list
   * reads. It is a presentation decision throughout: the server keeps and posts the
   * full precision it was given, and this decides what a screen prints.
   */
  readonly decimals: number;
  /**
   * The places a particular branch shows, keyed by branch identifier.
   *
   * Branches keep their own numbering and print formats, and a firm running a
   * wholesale branch beside a retail counter routinely wants different precision on
   * each: three places where goods are priced by the thousand, two at the till. A
   * branch with no entry here follows `decimals`.
   */
  readonly decimalsByBranch: Readonly<Record<string, number>>;
  /** Number of decimal places for quantity values throughout the system (e.g. 2 or 3). */
  readonly quantityDecimals: number;
  /** Whether device attributes (IMEI, Serial Number, Warranty, Model) are enabled. */
  readonly enableDeviceAttributes: boolean;
  /** Default sales rate type: retail, wholesale, or maximum retail price (MRP). */
  readonly defaultSalesRateType: SalesRateType;
  /** Default tax mode: NT (Non-Tax), TAX (VAT), or GST. */
  readonly defaultTaxMode: 'NT' | 'TAX' | 'GST';
  /** Default customer for sales transactions. */
  readonly defaultCustomerId: string;
  /** Default supplier for purchase transactions. */
  readonly defaultSupplierId: string;
  /** Default salesman identifier. */
  readonly defaultSalesman: string;
  /** Default billingman identifier. */
  readonly defaultBillingman: string;
  /** Whether reverse calculation is enabled on transaction entry. */
  readonly enableReverseCalculation: boolean;
  /** Whether free quantity column is enabled in sales entry. */
  readonly enableFreeQuantity: boolean;
  /** Whether per-item discount column is enabled in sales entry. */
  readonly enableItemDiscount: boolean;
  /** Whether automatic FIFO batch assignment is enabled. */
  readonly enableAutoBatch: boolean;
  /**
   * Whether serial numbers (IMEI) are offered on documents and products at all.
   *
   * Off by default: a firm that does not track handsets by number should never be
   * asked for one. On, the numbers are offered where a product is tracked by serial
   * number and are always optional.
   */
  readonly enableSerialNumbers: boolean;
  /** Default item type (1: Stocked, 2: Service, 3: Non-Stock). */
  readonly defaultItemType: number;
  /** Default brand identifier. */
  readonly defaultBrandId: string;
  /** Default debit account for payment vouchers. */
  readonly defaultDebitAccountId: string;
  /** Default credit account for receipt vouchers. */
  readonly defaultCreditAccountId: string;
  /**
   * The cash account a sale paid in cash is received into. Blank takes the firm's first
   * cash account, which is the only one most shops have.
   */
  readonly defaultCashLedgerId: string;
  /**
   * The bank account an online payment - Google Pay, a card - is received into, and the
   * bank a bank receipt or payment starts at. Blank takes the firm's first bank account.
   */
  readonly defaultBankLedgerId: string;
  /** Whether a receipt shows a bill-wise customer's pending bills. */
  readonly billWiseReceipts: boolean;
  /** Whether a receipt lets those bills be ticked and allocated, filling its amount. */
  readonly fillBillsOnReceipts: boolean;
  /** Whether a payment shows a bill-wise supplier's pending bills. */
  readonly billWisePayments: boolean;
  /** Whether a payment lets those bills be ticked and allocated, filling its amount. */
  readonly fillBillsOnPayments: boolean;
  /** Configured default additional ledgers per transaction type. */
  readonly defaultAdditionalLedgers: readonly DefaultAdditionalLedgerConfig[];
  /**
   * The theme every user starts in, as the firm chose it. Blank where it has not
   * chosen, which leaves it to the deployment: see src/lib/theme.ts.
   */
  readonly firmTheme: 'light' | 'dark' | '';
  /**
   * Whether the firm trades in the Gulf: Arabic names beside the English ones on the
   * masters, and documents that can be printed in both languages. Off, the Arabic
   * fields stay out of the way unless one already holds something.
   */
  readonly gulfMode: boolean;
  /**
   * Whether the firm charges tax at all. Off, every tax field and column - the tax
   * mode, the rate on a line, the tax on a bill, the tax rate on a product - stays out
   * of sight, and documents are entered without tax.
   */
  readonly taxEnabled: boolean;
  /** Whether sales and purchases offer Save draft beside posting; off, they only post. */
  readonly allowSaveDraft: boolean;
  /**
   * Whether the firm keeps stock in more than one godown. Off, no screen asks which
   * godown: every document uses the default one and the godown columns stay hidden.
   */
  readonly enableGodowns: boolean;
  /**
   * Whether a transaction is final the moment it is saved: orders and quotations are
   * confirmed and stock documents posted in the same click, and no draft is offered.
   */
  readonly directSave: boolean;
  /**
   * Whether the firm's Primary records have been made its defaults. Done once: a
   * default cleared afterwards is somebody's choice, not a gap to fill again.
   */
  readonly primaryDefaultsApplied: boolean;
  /**
   * Whether a sale shows the reference number box. Off, a sale takes its own number as
   * its reference, and one loaded from a quotation or an order keeps that one's.
   */
  readonly showSalesReference: boolean;
  /**
   * Whether a sale asks for its salesman and billing man. Off, nobody is asked: the sale
   * is credited to whoever is signed in and entered it.
   */
  readonly pickSalesmen: boolean;
  /** Whether a sale shows the name it is billed to, under the customer's details. */
  readonly showBillingName: boolean;
  /** Whether a sale shows the narration box, under the customer's details. */
  readonly showSalesNarration: boolean;
  /** Whether a sale shows the barcode box. */
  readonly showSalesScan: boolean;
  /** Whether a sale can be filled from a quotation, an order or a purchase. */
  readonly allowLoadFrom: boolean;
  /** Whether a sale can be put on hold and taken up again later in the day. */
  readonly allowHold: boolean;
  /** Whether the rate of a line, and the rate type of the bill, can be changed on a sale. */
  readonly allowRateChange: boolean;
  /** Whether a card, online or credit sale has to name a customer other than the Primary one. */
  readonly namedCustomerForNonCash: boolean;
  /**
   * The customer walk-in sales go to, where it is not the firm's Primary customer: a firm
   * that brought its customers in from another system has its cash customer among them,
   * and is given no Primary one. Blank, the Primary customer is the walk-in account.
   */
  readonly walkInCustomerId: string;
  /** The unit a sale's total is rounded to - 0.25, 1 - with the difference to Round Off. */
  readonly salesRoundTo: number;
  /**
   * How a new sale is paid, as the server numbers the modes: 2 cash, 4 card, 3 online,
   * 1 credit. Zero follows the vouchers' default: credit there is credit here, and
   * anything else is cash.
   */
  readonly defaultSalesPaymentMode: number;
  /** The ways a sale paid online can have been paid. */
  readonly onlinePaymentTypes: readonly OnlinePaymentType[];
  /**
   * The additional-ledger setups whose documents do not show additional ledgers at all,
   * by setup name: `Sales` for sales and returns, `SalesOrder`, `Purchase`, `PurchaseOrder`.
   */
  readonly hiddenChargeSetups: readonly string[];
}

export interface DefaultAdditionalLedgerConfig {
  readonly id: string;
  readonly transactionType: string;
  readonly ledgerId: string;
  readonly isAddition: boolean;
  readonly defaultAmount: string;
  readonly isDefaultActive: boolean;
}

/** The places a firm can choose between. Nothing sensible lies outside it. */
export const DECIMAL_CHOICES: readonly number[] = [0, 1, 2, 3, 4];

/** The rates a GST firm charges, and the rates a VAT firm does. */
export const GST_RATES: readonly number[] = [0, 0.25, 3, 5, 12, 18, 28];

/** The Gulf's VAT rates. Two, and one of them is zero. */
export const VAT_RATES: readonly number[] = [0, 5];

const DEFAULTS: AppSettings = {
  autoTranslateArabic: false,
  translationApiKey: '',
  taxRegime: TaxRegime.gccVat,
  taxEnabled: true,
  allowSaveDraft: true,
  enableGodowns: true,
  directSave: false,
  primaryDefaultsApplied: false,
  firmStateCode: '',
  taxRates: VAT_RATES,
  defaultTaxRate: 5,
  preferredWarehouseId: '',
  defaultCategoryId: '',
  defaultStockUnitId: '',
  defaultPaymentMode: '',
  defaultCharges: NO_DEFAULT_CHARGES,
  pageSize: 25,
  decimals: 2,
  decimalsByBranch: {},
  quantityDecimals: 2,
  enableDeviceAttributes: true,
  defaultSalesRateType: 'retail',
  defaultTaxMode: 'TAX',
  defaultCustomerId: '',
  defaultSupplierId: '',
  defaultSalesman: '',
  defaultBillingman: '',
  enableReverseCalculation: true,
  enableFreeQuantity: true,
  enableItemDiscount: true,
  enableAutoBatch: true,
  enableSerialNumbers: false,
  defaultItemType: 1,
  defaultBrandId: '',
  defaultDebitAccountId: '',
  defaultCreditAccountId: '',
  defaultCashLedgerId: '',
  defaultBankLedgerId: '',
  billWiseReceipts: true,
  fillBillsOnReceipts: true,
  billWisePayments: true,
  fillBillsOnPayments: true,
  defaultAdditionalLedgers: [],
  firmTheme: '',
  gulfMode: true,
  showSalesReference: false,
  pickSalesmen: false,
  showBillingName: true,
  showSalesNarration: true,
  showSalesScan: true,
  allowLoadFrom: true,
  allowHold: true,
  allowRateChange: true,
  namedCustomerForNonCash: true,
  walkInCustomerId: '',
  salesRoundTo: 0,
  defaultSalesPaymentMode: 0,
  onlinePaymentTypes: [],
  hiddenChargeSetups: [],
};

/**
 * Brings settings written by an earlier release up to date.
 *
 * The standing charges each kind of document starts with were once kept in a second,
 * simpler list; they now live with the additional-ledger defaults, and an older copy's
 * list is folded in rather than left applying where nothing can show or change it.
 */
function upgrade(stored: AppSettings): AppSettings {
  const settings = withFirmTheme(stored);
  const legacy = Object.entries(settings.defaultCharges ?? {}).filter(
    ([, rows]) => rows.length > 0,
  );

  if (legacy.length === 0) {
    return settings;
  }

  const kinds: Record<string, string> = {
    sales: 'Sales',
    salesReturn: 'SalesReturn',
    salesOrder: 'SalesOrder',
    purchase: 'Purchase',
    purchaseReturn: 'PurchaseReturn',
    purchaseOrder: 'PurchaseOrder',
  };

  const folded: DefaultAdditionalLedgerConfig[] = legacy.flatMap(([kind, rows]) =>
    rows
      .filter((row) => row.ledgerId)
      .map((row, index) => ({
        id: `legacy-${kind}-${index}`,
        transactionType: kinds[kind] ?? kind,
        ledgerId: row.ledgerId,
        isAddition: true,
        defaultAmount: row.amount,
        isDefaultActive: true,
      })),
  );

  return {
    ...settings,
    defaultCharges: NO_DEFAULT_CHARGES,
    defaultAdditionalLedgers: [...(settings.defaultAdditionalLedgers ?? []), ...folded],
  };
}

/**
 * Reads the firm's theme from where an earlier release kept it.
 *
 * That was `defaultTheme`, written with every save and light unless somebody changed
 * it - so light there cannot be told from nobody having chosen, and is read as nobody
 * having chosen, which lets the deployment's default show. Dark was always a choice.
 */
function withFirmTheme(settings: AppSettings): AppSettings {
  const { defaultTheme, ...rest } = settings as AppSettings & {
    readonly defaultTheme?: unknown;
  };

  if (defaultTheme === undefined) {
    return settings;
  }

  return {
    ...rest,
    firmTheme: rest.firmTheme || (defaultTheme === 'dark' ? 'dark' : ''),
  };
}

const STORAGE_KEY = 'erp.settings';

function read(): AppSettings {
  try {
    const stored = localStorage.getItem(STORAGE_KEY);

    if (!stored) {
      return DEFAULTS;
    }

    // Merged over the defaults rather than trusted whole: a stored blob written by
    // an older release is missing whatever has been added since, and spreading it
    // over the defaults is what stops a new setting arriving as `undefined`.
    return upgrade({ ...DEFAULTS, ...(JSON.parse(stored) as Partial<AppSettings>) });
  } catch {
    return DEFAULTS;
  }
}

/**
 * Settings that stay with this browser instead of following the firm.
 *
 * How many rows a list shows is a question about the screen in front of somebody,
 * and a laptop and a wall-mounted counter display want different answers.
 */
const DEVICE_ONLY: readonly (keyof AppSettings)[] = ['pageSize'];

/** The name the firm's copy is stored under on the server. */
const SERVER_KEY = 'preferences';

/** Where the firm's copy stands, for the screen that edits it. */
export type SettingsSync =
  /** Not yet read from the server: what is on screen is this browser's copy. */
  | 'local'
  /** Read from, and kept in step with, the firm's copy. */
  | 'firm'
  /** The firm's copy could be read but this user may not change it. */
  | 'read-only';

interface SettingsState extends AppSettings {
  /** Where these settings came from, and whether changes reach the firm. */
  readonly sync: SettingsSync;
  /** Changes some of the settings and writes them back. */
  update: (patch: Partial<AppSettings>) => void;
  /** Puts everything back to what a fresh installation starts with. */
  reset: () => void;
  /**
   * Replaces this browser's copy with the firm's.
   *
   * Called once somebody is signed in. A firm that has never saved its settings is
   * seeded from the first workstation that has, so the counter that was set up
   * before this change carries its setup to every other one.
   */
  hydrate: () => Promise<void>;
}

/** The part of the settings the firm shares. */
function shared(settings: AppSettings): Partial<AppSettings> {
  const copy: Record<string, unknown> = { ...settings };

  for (const key of DEVICE_ONLY) {
    delete copy[key];
  }

  return copy as Partial<AppSettings>;
}

/** Only the settings themselves, without the store's own fields and actions. */
function settingsOf(state: SettingsState): AppSettings {
  const copy: Record<string, unknown> = {};

  for (const key of Object.keys(DEFAULTS) as (keyof AppSettings)[]) {
    copy[key] = state[key];
  }

  return copy as unknown as AppSettings;
}

function store(settings: AppSettings): void {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(settings));
  } catch {
    // A browser with storage disabled still gets the setting for this session.
    // Losing it on reload is a smaller failure than refusing to apply it.
  }
}

let pending: number | undefined;

/**
 * Writes the firm's copy, a moment after the last change.
 *
 * Debounced so a field typed into a character at a time is one request, not one per
 * keystroke. A refusal is remembered rather than retried: a user without the right
 * to change the firm's settings keeps their changes in this browser, and the screen
 * says so.
 */
function saveToFirm(settings: AppSettings, onRefused: () => void): void {
  window.clearTimeout(pending);
  pending = window.setTimeout(() => {
    void request<void>(`/settings/${SERVER_KEY}`, {
      method: 'PUT',
      body: { value: JSON.stringify(shared(settings)) },
    }).catch((failure: unknown) => {
      if (failure instanceof ApiError && failure.status === 403) {
        onRefused();
      }
    });
  }, 600);
}

export const useSettings = create<SettingsState>((set, get) => ({
  ...read(),
  sync: 'local',

  update: (patch) => {
    const next: AppSettings = {
      ...settingsOf(get()),
      ...patch,
    };

    set(next);
    store(next);

    if (get().sync === 'firm') {
      saveToFirm(next, () => set({ sync: 'read-only' }));
    }
  },

  reset: () => {
    set(DEFAULTS);

    try {
      localStorage.removeItem(STORAGE_KEY);
    } catch {
      // As above.
    }

    if (get().sync === 'firm') {
      saveToFirm(DEFAULTS, () => set({ sync: 'read-only' }));
    }
  },

  hydrate: async () => {
    let held: string | null;

    try {
      held = (await request<{ readonly value: string | null }>(`/settings/${SERVER_KEY}`))
        .value;
    } catch {
      // No firm selected, or the server is unreachable: carry on with this browser's
      // copy, which is what every screen used before there was a firm copy at all.
      return;
    }

    const local = settingsOf(get());

    if (held === null) {
      // Nobody has saved the firm's settings yet. If this browser was set up, its
      // setup becomes the firm's; if the user may not do that, it stays local.
      set({ sync: 'firm' });

      if (JSON.stringify(shared(local)) !== JSON.stringify(shared(DEFAULTS))) {
        saveToFirm(local, () => set({ sync: 'read-only' }));
      }

      return;
    }

    let firm: Partial<AppSettings>;

    try {
      firm = JSON.parse(held) as Partial<AppSettings>;
    } catch {
      return;
    }

    const device: Partial<AppSettings> = {};

    for (const key of DEVICE_ONLY) {
      (device as Record<string, unknown>)[key] = local[key];
    }

    // Over the defaults, as `read` does, so a setting added since the firm last saved
    // arrives at its default rather than as `undefined`.
    const next: AppSettings = upgrade({ ...DEFAULTS, ...firm, ...device });

    set({ ...next, sync: 'firm' });
    store(next);
  },
}));

/** The rates that suit a regime, for the screen that resets them. */
export function ratesFor(regime: number): readonly number[] {
  return regime === TaxRegime.indiaGst ? GST_RATES : VAT_RATES;
}

/** Whether tax is shown and charged: switched on in Settings, under a regime that has it. */
export function useTaxOn(): boolean {
  return useSettings(
    (state) => state.taxEnabled !== false && state.taxRegime !== TaxRegime.none,
  );
}

/** Whether godowns are in use; off, the default godown is used and never asked for. */
export function useGodownsOn(): boolean {
  return useSettings((state) => state.enableGodowns !== false);
}

/** The column keys that say where stock is held. */
const PLACE_COLUMNS: ReadonlySet<string> = new Set(['warehouse', 'godown', 'branch']);

/**
 * A report's columns, less the ones naming a godown or its branch while godowns are
 * switched off: with one godown they would say the same thing on every row.
 */
export function useGodownColumns<T extends { readonly key: string }>(
  columns: readonly T[],
): readonly T[] {
  const godownsOn = useGodownsOn();

  return godownsOn ? columns : columns.filter((column) => !PLACE_COLUMNS.has(column.key));
}

/** Whether a transaction is final the moment it is saved, with no draft or confirm step. */
export function useDirectSave(): boolean {
  return useSettings((state) => state.directSave === true);
}

/**
 * How a new sale is paid, as the server numbers the modes: the firm's choice, or - where
 * it has made none - credit when its receipts default to credit, and cash otherwise.
 */
export function salesPaymentModeOf(
  settings: Pick<AppSettings, 'defaultSalesPaymentMode' | 'defaultPaymentMode'>,
): number {
  if ([1, 2, 3, 4].includes(settings.defaultSalesPaymentMode)) {
    return settings.defaultSalesPaymentMode;
  }

  return settings.defaultPaymentMode === 'Credit' ? 1 : 2;
}

/**
 * Whether a kind of document shows its additional ledgers, by the name of the setup it
 * draws on: `Sales` for sales and their returns, `SalesOrder`, `Purchase`, `PurchaseOrder`.
 */
export function useChargesShown(setup: string): boolean {
  return useSettings((state) => !(state.hiddenChargeSetups ?? []).includes(setup));
}
